import React, { useState, useEffect, useRef } from 'react';
import PromptCard from '../components/PromptCard';
import CategoryBar from '../components/CategoryBar';
import PromptForm from '../components/PromptForm';
import PromptModal from '../components/PromptModal';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { Prompt, Category } from '../types';
import { getPromptsPage, getCategories, deletePrompt, deleteCategory } from '../utils/firebaseService';
import { X, Search } from 'lucide-react';
import { User } from 'firebase/auth';
import { translations } from '../locales/translations';
import { auth } from '../config/firebase';
import { logout } from '../services/authService';
import MobileBottomNav from '../components/MobileBottomNav';
import Toast, { ToastTone } from '../components/feedback/Toast';
import ErrorModal from '../components/feedback/ErrorModal';
import LoadingScreen from '../components/feedback/LoadingScreen';
import {

  doc,

  getDoc

  , type DocumentData,
  type QueryDocumentSnapshot

} from 'firebase/firestore';

import {

  db

} from '../config/firebase';

interface HomePageProps {
  user: User;
}

const HomePage: React.FC<HomePageProps> = ({ user }) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [allCategories,
  setAllCategories] =
  useState<Category[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | undefined>();
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | undefined>();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMorePrompts, setHasMorePrompts] = useState(false);
  const [promptCursor, setPromptCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; tone: ToastTone } | null>(null);
  const [confirmation, setConfirmation] = useState<{ title: string; message: string; onConfirm: () => Promise<void> } | null>(null);
  const [hideSensitive, setHideSensitive] = useState(true);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [mode, setMode] = useState<'personal' | 'global'>('personal');
  const [userRole, setUserRole] =

  useState('user');
  const currentUser = auth.currentUser;

  const [isMobile, setIsMobile] =
  useState(false);

  
const [
  showProfileMenu,
  setShowProfileMenu
] = useState(false);

if (!currentUser) {
  throw new Error('Usuário não autenticado');
}

const [profileOpen, setProfileOpen] = useState(false);

const [settingsOpen, setSettingsOpen] = useState(false);
  const initialLoadRef = useRef(true);
  const promptRequestRef = useRef(0);

const userId = currentUser.uid; // TODO: implementar autenticação real

  // Language setup
  const [language, setLanguage] = useState(
  localStorage.getItem('language') || 'pt'
);
  const t = translations[language as keyof typeof translations];

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {

  const fetchUserRole =
    async () => {

      try {

        const currentUser =
          auth.currentUser;

        if (!currentUser)
          return;

        const userRef = doc(

          db,

          'users',

          currentUser.uid
        );

        const userSnap =
          await getDoc(userRef);

        if (userSnap.exists()) {

          const userData =
            userSnap.data();

          setUserRole(

            userData.role || 'user'
          );
        }

      } catch (error) {

        console.error(
          'Erro ao buscar role:',
          error
        );
      }
  };

  fetchUserRole();

}, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([loadPrompts(), loadCategories()]);
    } catch (error) {
      console.error(t.errorLoadingData, error);
      setFeedback({ message: t.errorLoadingData, tone: 'error' });
    } finally {
      initialLoadRef.current = false;
      setIsLoading(false);
    }
  };


  useEffect(() => {
    if (initialLoadRef.current) return;
    loadCategories();
  }, [mode]);

  useEffect(() => {
    if (initialLoadRef.current) return;
    loadPrompts();
  }, [selectedCategory, debouncedSearchQuery, mode]);


useEffect(() => {
  const syncSensitiveContent = () => {
    setHideSensitive(
      localStorage.getItem('hideSensitive') === 'true'
    );
  };

  window.addEventListener(
    'sensitive-content-change',
    syncSensitiveContent
  );

  return () => {
    window.removeEventListener(
      'sensitive-content-change',
      syncSensitiveContent
    );
  };
}, []);
useEffect(() => {
  const syncLanguage = () => {
    setLanguage(
      localStorage.getItem('language') || 'pt'
    );
  };

  window.addEventListener(
    'language-change',
    syncLanguage
  );

  return () => {
    window.removeEventListener(
      'language-change',
      syncLanguage
    );
  };
}, []);

useEffect(() => {

  const checkMobile = () => {

    setIsMobile(
      window.innerWidth < 768
    );

  };

  checkMobile();

  window.addEventListener(
    'resize',
    checkMobile
  );

  return () => {

    window.removeEventListener(
      'resize',
      checkMobile
    );

  };

}, []);
  const loadPrompts = async (append = false) => {
    const requestId = promptRequestRef.current + 1;
    promptRequestRef.current = requestId;

    try {
      
      const page = await getPromptsPage({
        
        category: selectedCategory,
        search: debouncedSearchQuery,
        sortBy: 'newest',
        mode: mode,
        userId: auth.currentUser?.uid,
      }, 24, append ? promptCursor : null);
      
      if (requestId === promptRequestRef.current) {
        setPrompts((currentPrompts) => append ? [...currentPrompts, ...page.prompts] : page.prompts);
        setPromptCursor(page.cursor);
        setHasMorePrompts(page.hasMore);
      }
    } catch (error) {
      console.error(t.errorLoadingPrompts, error);
      setFeedback({ message: t.errorLoadingPrompts, tone: 'error' });
    }
  };

  const loadCategories = async () => {
    try {
      const data =
  await getCategories(userId);
  setAllCategories(data);

const filteredData =
  mode === 'global'
    ? data.filter(
        category =>
          category.type === 'global'
      )
    : data.filter(
        category =>
          category.type === 'personal'
          &&
          category.ownerId === userId
      );

setCategories(filteredData);
    } catch (error) {
      console.error(t.errorLoadingCategories, error);
      setFeedback({ message: t.errorLoadingCategories, tone: 'error' });
    }
  };

  const handleNewPrompt = () => {
    setEditingPrompt(undefined);
    setShowForm(true);
  };

  const handleEditPrompt = (prompt: Prompt) => {
    setSelectedPrompt(undefined);
    setShowModal(false);
    setEditingPrompt(prompt);
    setShowForm(true);
  };

  const deletePromptConfirmed = async (id: string) => {
    try {
      await deletePrompt(
  id,
  currentUser.uid
);
      setPrompts(prompts.filter((p) => p.id !== id));
      setFeedback({ message: 'Prompt excluido com sucesso.', tone: 'success' });
    } catch (error) {
      console.error(t.errorDeletingPrompt, error);
      setFeedback({ message: t.errorDeletingPrompt, tone: 'error' });
    }
  };

  const handleLoadMorePrompts = async () => {
    if (!hasMorePrompts || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      await loadPrompts(true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleDeletePrompt = (id: string) => {
    setConfirmation({ title: 'Excluir prompt', message: t.confirmDeletePrompt, onConfirm: () => deletePromptConfirmed(id) });
  };

  const deleteCategoryConfirmed = async (category: Category) => {
    if (category.name === 'Todos') return;
    try {
      await deleteCategory(category.id);
      if (selectedCategory === category.name) {
        setSelectedCategory(undefined);
      }
      await loadCategories();
      await loadPrompts();
      setFeedback({ message: 'Categoria excluida com sucesso.', tone: 'success' });
    } catch (error: any) {

  if (
    error.message ===
    'CATEGORY_HAS_PROMPTS'
  ) {

    setFeedback({
      message: 'Esta categoria possui prompts vinculados. Remova ou mova os prompts antes de excluí-la.',
      tone: 'error',
    });

    return;
  }

  console.error(
    t.errorDeletingCategory,
    error
  );
  setFeedback({ message: t.errorDeletingCategory, tone: 'error' });

}
  };

  const handleDeleteCategory = (category: Category) => {
    if (category.name === 'Todos') return;
    setConfirmation({
      title: 'Excluir categoria',
      message: `${t.confirmDeleteCategory} "${category.name}"${t.confirmDeleteCategorySuffix}`,
      onConfirm: () => deleteCategoryConfirmed(category),
    });
  };
  
  const handlePromptClick = (prompt: Prompt) => {
    setSelectedPrompt(prompt);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPrompt(undefined);
  };

  const handleOpenCategoryFilter = () => {
    setShowCategoryFilter(true);
  };

  const handleCloseCategoryFilter = () => {
    setShowCategoryFilter(false);
  };

  const handleModeChange = (newMode: 'personal' | 'global') => {
    setMode(newMode);
  };

  const handleCategorySelect = (categoryName?: string) => {
    setSelectedCategory(categoryName);
    setShowCategoryFilter(false);
  };

  const handleSavePrompt = async () => {
    // O PromptForm agora faz o salvamento internamente
    setShowForm(false);
    setEditingPrompt(undefined);
    await loadPrompts();
    await loadCategories(); // Reload categories in case a new one was created
  };

  const visiblePrompts = prompts;

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingPrompt(undefined);
  };

  return (
    <div className="bg-dark-900 min-h-screen">
      {feedback && <Toast message={feedback.message} tone={feedback.tone} onClose={() => setFeedback(null)} />}
      <ErrorModal
        isOpen={Boolean(confirmation)}
        title={confirmation?.title ?? ''}
        message={confirmation?.message ?? ''}
        confirmLabel="Excluir"
        onClose={() => setConfirmation(null)}
        onConfirm={() => {
          const action = confirmation?.onConfirm;
          setConfirmation(null);
          void action?.();
        }}
        destructive
      />
      {!isMobile && (

  <Sidebar
    mode={mode}
    onModeChange={handleModeChange}
    onNewPrompt={handleNewPrompt}
  />

)}
      
      <Header
  onNewPrompt={handleNewPrompt}

  profileOpen={profileOpen}
  setProfileOpen={setProfileOpen}

  settingsOpen={settingsOpen}
  setSettingsOpen={setSettingsOpen}

  showNewButton={true}
  showLogout={false}
  user={user}
/>

      <div
  className={`
    main-content
    w-full
    px-1.5
    sm:px-4
    lg:px-8
    py-4
    sm:py-8
    pb-24

    
  `}
>
        {/* Top Bar - Search and Categories */}
        <div className="mb-8 space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.searchPrompts}
              className="w-full pl-10 pr-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:border-purple-500 transition-colors"
            />
          </div>

          {/* Categories Bar */}
          <CategoryBar
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            onDeleteCategory={handleDeleteCategory}
            onOpenFilter={handleOpenCategoryFilter}
            isAdmin={userRole === 'admin'}
          />
        </div>

        {/* Content Toggle */}
        <div className="mb-6 rounded-lg border border-dark-700 bg-dark-800 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-dark-300">
            {hideSensitive
              ? t.sensitiveContentHidden
              : t.sensitiveContentVisible}
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <span className="text-dark-100 font-medium">{t.hideSensitiveContent}</span>
            <button
              type="button"
              onClick={() => setHideSensitive((prev) => !prev)}
              className={`relative inline-flex h-6 w-12 items-center rounded-full transition-colors ${
                hideSensitive ? 'bg-purple-600' : 'bg-dark-700'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  hideSensitive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </label>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75">
            <div className="bg-dark-900 rounded-lg border border-dark-700 max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-dark-700">
                <h2 className="text-2xl font-bold text-dark-100">
                  {editingPrompt ? t.editPrompt : t.addNewPrompt}
                </h2>
                <button
                  onClick={handleCancelForm}
                  className="p-2 hover:bg-dark-700 rounded transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 overflow-y-auto">
                <PromptForm
                  onSubmit={handleSavePrompt}
                  initialData={editingPrompt}
                  categories={allCategories}
                  isLoading={false}
                  onCancel={handleCancelForm}
                  userId={userId}
                />
              </div>
            </div>
          </div>
        )}

        {/* Category Filter Modal */}
      {showCategoryFilter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75">
          <div className="bg-dark-900 rounded-lg border border-dark-700 max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-dark-700">
              <h2 className="text-xl font-semibold text-dark-100">{t.allCategories}</h2>
              <button
                type="button"
                onClick={handleCloseCategoryFilter}
                className="p-2 hover:bg-dark-700 rounded transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto space-y-3">
              <button
                type="button"
                onClick={() => handleCategorySelect(undefined)}
                className={`w-full text-left px-4 py-3 rounded-lg ${!selectedCategory ? 'bg-purple-600 text-white' : 'bg-dark-800 text-dark-200 hover:bg-dark-700'}`}
              >
                {t.all}
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleCategorySelect(category.name)}
                  className={`w-full text-left px-4 py-3 rounded-lg ${selectedCategory === category.name ? 'bg-purple-600 text-white' : 'bg-dark-800 text-dark-200 hover:bg-dark-700'}`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
        {isLoading ? (
          <LoadingScreen label="Carregando prompts..." />
        ) : visiblePrompts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-dark-300 text-lg mb-4">
              {searchQuery || selectedCategory ? 'Nenhum prompt corresponde aos filtros selecionados.' : t.noPromptsFound}
            </p>
            {!showForm && (
              <button
                onClick={handleNewPrompt}
                className="btn-primary"
              >
                {t.addFirstPrompt}
              </button>
            )}
          </div>
        ) : (
          <>
          <div
  className="
    grid
    grid-cols-1
    sm:grid-cols-2
    md:grid-cols-3
    lg:grid-cols-4

    gap-2
    sm:gap-5
    lg:gap-8

    md:mx-0
  "

>
            
            {prompts
  .filter(
    (prompt) => !prompt.isHidden
  )
  .map((prompt) => (

              <PromptCard
                key={prompt.id}
                prompt={prompt}
                categories={categories}
                hideSensitive={hideSensitive}
                onClick={handlePromptClick}
              />
            ))}
          </div>
          {hasMorePrompts && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={handleLoadMorePrompts}
                disabled={isLoadingMore}
                className="btn-primary disabled:opacity-50"
              >
                {isLoadingMore ? 'Carregando...' : 'Carregar mais'}
              </button>
            </div>
          )}
          </>
        )}
      </div>

      {/* Prompt Modal */}
      {selectedPrompt && (

  <PromptModal

  prompt={selectedPrompt}

  isOpen={showModal}

  onClose={handleCloseModal}

  onEdit={handleEditPrompt}

  onDelete={handleDeletePrompt}

  userRole={userRole}

  categories={categories}

  currentUserId={user.uid}

  onMarkerClick={(marker) => {

    setSearchQuery(marker);

    handleCloseModal();

  }}

 />

)}

{isMobile && !selectedPrompt && (

  <MobileBottomNav

    mode={mode}

    onModeChange={handleModeChange}

    user={user}

    onProfileClick={() => {

  setShowProfileMenu(
    (prev) => !prev
  );

}}


  />

  

)}

{showProfileMenu && (

  <div
    className="
      fixed
      bottom-24
      right-4
      z-50

      w-56

      rounded-2xl

      border
      border-dark-700

      bg-dark-900/75
      backdrop-blur-[20px]

      shadow-2xl

      overflow-hidden
    "
  >

    <button
    onClick={() => {

  setProfileOpen(true);

  setShowProfileMenu(false);

}}
      className="
        w-full
        px-4
        py-3

        text-left

        hover:bg-dark-700

        transition-colors
      "
    >
      Meu Perfil

    </button>

    <button
          onClick={() => {

  setSettingsOpen(true);

  setShowProfileMenu(false);

}}
      className="
        w-full
        px-4
        py-4

        text-left

        hover:bg-dark-700

        transition-colors
      "
    >
      Configurações

    </button>

    <button
    onClick={() => {

  setSettingsOpen(true);

  setShowProfileMenu(false);

}}
      className="
        w-full
        px-4
        py-4

        text-left

        hover:bg-dark-700

        transition-colors
      "
    >
      Idioma
    </button>

    <button
    onClick={async () => {

  setShowProfileMenu(false);

  await logout();

}}
      className="
        w-full
        px-4
        py-4

        text-left

        text-red-400

        hover:bg-dark-700

        transition-colors
      "
    >
      Sair da conta
    </button>

  </div>

)}


    </div>
  );
};

export default HomePage;
